import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type {
  AuditEventDto,
  BrowserProfileDto,
  CreateBrowserProfileBody,
  CreateSupportSessionBody,
  JoinResponse,
  NodeDto,
  SupportSessionDto,
  SupportSessionStatus,
  TabInfo,
  UpdateBrowserProfileBody,
  UserDto,
  UserRole,
} from '@br/protocol';
import { api, isApiError } from './client';

export interface MeResponse {
  user: UserDto;
  org: { id: string; name: string };
}
export interface UserListItem extends UserDto {
  disabled?: boolean;
}
export interface UserInviteCreated {
  token: string;
  url: string;
  expiresAt: string;
  role: UserRole;
  email: string | null;
}
export interface UserInviteInfo {
  email: string | null;
  role: UserRole;
  orgName: string;
  expiresAt: string;
}
export interface JoinMe extends JoinResponse {
  status: SupportSessionStatus;
}
export interface CreateProfileResponse {
  profile: BrowserProfileDto;
  supportSession: SupportSessionDto | null;
  openError: string | null;
}

export const qk = {
  me: ['me'] as const,
  profiles: (archived: boolean) => ['profiles', { archived }] as const,
  profile: (id: string) => ['profile', id] as const,
  supportSessions: (id: string) => ['profile', id, 'support-sessions'] as const,
  profileEvents: (id: string) => ['profile', id, 'events'] as const,
  nodes: ['nodes'] as const,
  users: ['users'] as const,
  userInvite: (token: string) => ['user-invite', token] as const,
  joinMe: ['join', 'me'] as const,
};

// ---------------------------------------------------------------- auth

export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: async (): Promise<MeResponse | null> => {
      try {
        return await api<MeResponse>('GET', '/api/auth/me');
      } catch (error) {
        if (isApiError(error) && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api<{ user: UserDto }>('POST', '/api/auth/login', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.me }),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api('POST', '/api/auth/logout'),
    onSettled: () => {
      queryClient.setQueryData(qk.me, null);
      queryClient.clear();
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { current: string; next: string }) =>
      api('POST', '/api/auth/password', body),
  });
}

// ---------------------------------------------------------------- browser profiles

export interface ProfilePage {
  profiles: BrowserProfileDto[];
  total: number;
  page: number;
  pageSize: number;
}

export function useProfiles(archived: boolean, page = 1, q = '') {
  return useQuery({
    queryKey: [...qk.profiles(archived), { page, q }],
    queryFn: async () =>
      api<{ profiles: BrowserProfileDto[]; total: number; page: number; pageSize: number }>(
        'GET',
        `/api/profiles?${new URLSearchParams({ archived: String(archived), page: String(page), q })}`,
      ),
    refetchInterval: 15_000,
  });
}

export function useProfile(id: string) {
  return useQuery({
    queryKey: qk.profile(id),
    queryFn: async () =>
      (await api<{ profile: BrowserProfileDto }>('GET', `/api/profiles/${id}`)).profile,
    refetchInterval: 15_000,
  });
}

function invalidateProfiles(queryClient: QueryClient, id?: string) {
  void queryClient.invalidateQueries({ queryKey: ['profiles'] });
  if (id) void queryClient.invalidateQueries({ queryKey: qk.profile(id) });
}

export function useCreateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBrowserProfileBody) =>
      api<CreateProfileResponse>('POST', '/api/profiles', body),
    onSuccess: () => invalidateProfiles(queryClient),
  });
}

export function useUpdateProfile(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateBrowserProfileBody) =>
      (await api<{ profile: BrowserProfileDto }>('PATCH', `/api/profiles/${id}`, body)).profile,
    onSuccess: (profile) => {
      queryClient.setQueryData(qk.profile(id), profile);
      invalidateProfiles(queryClient);
    },
  });
}

export type ProfileAction = 'open' | 'close';

export function useProfileAction(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: ProfileAction) =>
      (await api<{ profile: BrowserProfileDto }>('POST', `/api/profiles/${id}/browser/${action}`))
        .profile,
    onSuccess: (profile) => {
      queryClient.setQueryData(qk.profile(id), profile);
      invalidateProfiles(queryClient);
    },
    onError: () => invalidateProfiles(queryClient, id),
  });
}

export function useDeleteProfileData(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api<{ profile: BrowserProfileDto }>('DELETE', `/api/profiles/${id}/data`)).profile,
    onSuccess: (profile) => {
      queryClient.setQueryData(qk.profile(id), profile);
      invalidateProfiles(queryClient);
    },
  });
}

export function useArchiveProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api<{ profile: BrowserProfileDto }>('DELETE', `/api/profiles/${id}`)).profile,
    onSuccess: (profile) => {
      queryClient.setQueryData(qk.profile(profile.id), profile);
      void queryClient.invalidateQueries({ queryKey: qk.supportSessions(profile.id) });
      invalidateProfiles(queryClient);
    },
  });
}

export function useEgressIp(id: string) {
  return useMutation({
    mutationFn: () => api<{ ip?: string; error?: string }>('POST', `/api/profiles/${id}/egress-ip`),
  });
}

export function useSupportSessions(id: string) {
  return useQuery({
    queryKey: qk.supportSessions(id),
    queryFn: async () =>
      (
        await api<{ supportSessions: SupportSessionDto[] }>(
          'GET',
          `/api/profiles/${id}/support-sessions`,
        )
      ).supportSessions,
  });
}

export function useCreateSupportSession(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<CreateSupportSessionBody> = {}) =>
      (
        await api<{ supportSession: SupportSessionDto }>(
          'POST',
          `/api/profiles/${id}/support-sessions`,
          body,
        )
      ).supportSession,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.supportSessions(id) });
      invalidateProfiles(queryClient, id);
    },
  });
}

export function useUpdateSupportSession(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      supportSessionId,
      status,
    }: {
      supportSessionId: string;
      status: SupportSessionStatus;
    }) =>
      (
        await api<{ supportSession: SupportSessionDto }>(
          'PATCH',
          `/api/profiles/${id}/support-sessions/${supportSessionId}`,
          { status },
        )
      ).supportSession,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.supportSessions(id) });
      invalidateProfiles(queryClient, id);
    },
  });
}

export function useRevokeSupportSession(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (supportSessionId: string) =>
      api('DELETE', `/api/profiles/${id}/support-sessions/${supportSessionId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.supportSessions(id) });
      invalidateProfiles(queryClient, id);
    },
  });
}

const EVENT_PAGE = 50;

export function useProfileEvents(id: string) {
  return useInfiniteQuery({
    queryKey: qk.profileEvents(id),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const query = new URLSearchParams({ limit: String(EVENT_PAGE) });
      if (pageParam) query.set('before', pageParam);
      return (await api<{ events: AuditEventDto[] }>('GET', `/api/profiles/${id}/events?${query}`))
        .events;
    },
    getNextPageParam: (last) => (last.length < EVENT_PAGE ? undefined : last[last.length - 1]?.id),
  });
}

// ---------------------------------------------------------------- nodes

export function useNodes() {
  return useQuery({
    queryKey: qk.nodes,
    queryFn: async () => (await api<{ nodes: NodeDto[] }>('GET', '/api/nodes')).nodes,
    refetchInterval: 15_000,
  });
}

export function useRenameNode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api<{ node: NodeDto }>('PATCH', `/api/nodes/${id}`, { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.nodes }),
  });
}

export function useDeleteNode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api('DELETE', `/api/nodes/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.nodes }),
  });
}

export function useRotateNodeToken() {
  return useMutation({
    mutationFn: async (id: string) =>
      (await api<{ token: string }>('POST', `/api/nodes/${id}/token`)).token,
  });
}

// ---------------------------------------------------------------- users

export function useUsers() {
  return useQuery({
    queryKey: qk.users,
    queryFn: async () => (await api<{ users: UserListItem[] }>('GET', '/api/users')).users,
  });
}

export function useCreateUserInvite() {
  return useMutation({
    mutationFn: async (body: { email?: string; role: UserRole }) =>
      (await api<{ invite: UserInviteCreated }>('POST', '/api/users/invites', body)).invite,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string;
      role?: UserRole;
      disabled?: boolean;
      name?: string;
    }) => api<{ user: UserDto }>('PATCH', `/api/users/${id}`, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.users }),
  });
}

export function useUserInvite(token: string) {
  return useQuery({
    queryKey: qk.userInvite(token),
    queryFn: async () =>
      (await api<{ invite: UserInviteInfo }>('GET', `/api/users/invites/${token}`)).invite,
    retry: false,
  });
}

export function useAcceptUserInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { token: string; name: string; email: string; password: string }) =>
      api<{ user: UserDto }>('POST', '/api/users/invites/accept', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.me }),
  });
}

// ---------------------------------------------------------------- customer join

export function useJoinMe() {
  return useQuery({
    queryKey: qk.joinMe,
    queryFn: async (): Promise<JoinMe | null> => {
      try {
        return await api<JoinMe>('GET', '/api/join/me');
      } catch (error) {
        if (isApiError(error) && (error.status === 401 || error.status === 404)) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
}

export function useJoin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { token?: string; code?: string; label: string }) =>
      api<JoinResponse>('POST', '/api/join', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.joinMe }),
  });
}

export function useLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api('POST', '/api/join/leave'),
    onSettled: () => queryClient.setQueryData(qk.joinMe, null),
  });
}

// ---------------------------------------------------------------- cache patching from /ws/app

export function patchProfileInCaches(
  queryClient: QueryClient,
  profileId: string,
  patch: (profile: BrowserProfileDto) => BrowserProfileDto,
): void {
  queryClient.setQueryData<BrowserProfileDto>(qk.profile(profileId), (profile) =>
    profile ? patch(profile) : profile,
  );
  queryClient.setQueriesData<ProfilePage>({ queryKey: ['profiles'] }, (list) =>
    list
      ? {
          ...list,
          profiles: list.profiles.map((profile) =>
            profile.id === profileId ? patch(profile) : profile,
          ),
        }
      : list,
  );
}

export function patchProfileTabs(
  queryClient: QueryClient,
  profileId: string,
  tabs: TabInfo[],
): void {
  patchProfileInCaches(queryClient, profileId, (profile) => ({
    ...profile,
    tabs,
    activeTabId: tabs.find((tab) => tab.active)?.tabId ?? profile.activeTabId,
  }));
}

export function patchNodeOnline(queryClient: QueryClient, nodeId: string, online: boolean): void {
  queryClient.setQueryData<NodeDto[]>(qk.nodes, (list) =>
    list ? list.map((node) => (node.id === nodeId ? { ...node, online } : node)) : list,
  );
  queryClient.setQueriesData<ProfilePage>({ queryKey: ['profiles'] }, (list) =>
    list
      ? {
          ...list,
          profiles: list.profiles.map((profile) =>
            profile.nodeId === nodeId ? { ...profile, nodeOnline: online } : profile,
          ),
        }
      : list,
  );
  queryClient.setQueriesData<BrowserProfileDto>({ queryKey: ['profile'] }, (profile) =>
    profile && profile.nodeId === nodeId ? { ...profile, nodeOnline: online } : profile,
  );
}
